import { ITourChip, ITourDetails, IToursJSON, IXMLHttpConfig, TourType, TourTypeCode } from "../global/types";
import { XMLHttpConfig } from "../global/XMLHttpConfig";
import { HttpService } from "../services/HttpService";

declare var _gaq: any;

export class ToursMainStage {
    private tourStageLoadedForType: TourType;
    private httpSvc: HttpService;
    private isLoading: boolean;
    private tourTypes: string[];
    private tourLocation: string;
    private headingTextArray: string[];

    constructor() {
        this.isLoading = false;
        this.tourStageLoadedForType = TourType.NONE;
        this.tourLocation = "";
        this.tourTypes = ["N", "E", "C", "D", "O"];
        this.headingTextArray = ["", "Escorted Tours", "Cruise Holidays", "Self-drive Tours", "Private Tours"];
        this.httpSvc = new HttpService();
        this.addTourTypeEventListeners();

        window.addEventListener("scroll", () => { this.infinityScroll(); }, false);
    }

    private async getTours(createHeading?: boolean, noClear?: boolean) {
        this.toggleLoading(true);
        this.toggleInfinityScrollTrigger(false);

        if (!noClear) {
            this.clearStage();
        }

        if (createHeading) {
            this.createTourStageHeading();
        }

        const response: IToursJSON | any = await this.httpSvc.get(new XMLHttpConfig(this.generateRequestURL()));
        this.handleResponse(response);
    }

    private handleResponse(data: IToursJSON) {
        this.toggleLoading(false);
        this.toggleInfinityScrollTrigger(false);

        if (data.TourChips) {
            this.createLocationSelect(data.TourChips);
        }

        if (data.TourDetails && data.TourDetails.length > 0) {
            this.appendTourHTML(data.TourDetails);
            this.toggleInfinityScrollTrigger(true);
        }

        if (data.IsTheLastOf) {
            this.toggleInfinityScrollTrigger(false);
        }
    }

    // HTML Building Functions.

    private appendTourHTML(data: ITourDetails[]) {
        const clearfixOffset: number = this.countToursLoaded();
        const stage: HTMLElement = document.querySelector("[data-main-tours-stage]") as HTMLElement;

        for (let i = 0; i < data.length; i++) {
            const container: HTMLElement = document.createElement("a");
            const header: HTMLElement = document.createElement("h4");
            const image: HTMLElement = document.createElement("img");
            const description: HTMLElement = document.createElement("p");
            const footer: HTMLElement = document.createElement("h5");

            header.innerText = data[i].TourName;
            image.setAttribute("src", data[i].TourImageURL);
            description.innerText = data[i].DescriptionHeader;
            footer.innerHTML = `${data[i].Days} days fr <strong>£${data[i].Price}pp</strong>`;

            container.classList.add("tour-tour");
            container.classList.add("block");
            container.classList.add("col-xxs-12");
            container.classList.add("col-xs-6");
            container.classList.add("col-sm-4");
            container.setAttribute("href", data[i].TourURL);
            container.setAttribute("data-main-tours-tour", data[i].TourId.toString());

            container.appendChild(header);
            container.appendChild(image);
            container.appendChild(description);
            container.appendChild(footer);

            stage.appendChild(container);

            if ((clearfixOffset + i + 1) % 3 === 0) {
                const rowBreakFor3: HTMLElement = document.createElement("div") as HTMLElement;
                rowBreakFor3.classList.add("clearfix");
                rowBreakFor3.classList.add("hidden-xs");
                stage.appendChild(rowBreakFor3);
            }

            if ((clearfixOffset + i + 1) % 2 === 0) {
                const rowBreakFor2: HTMLElement = document.createElement("div") as HTMLElement;
                rowBreakFor2.classList.add("clearfix");
                rowBreakFor2.classList.add("visible-xs");
                stage.appendChild(rowBreakFor2);
            }
        }
    }

    private createLocationSelect(data: ITourChip[]) {
        const heading: HTMLElement = document.querySelector("[data-main-tours-heading] h2") as HTMLElement;
        heading.innerText = heading.innerText + " in ";

        const selectBox: HTMLElement = document.createElement("select");

        for (let i = 0; i < data.length; i++) {
            if (data[i].Count > 0) {
                const option: HTMLElement = document.createElement("option");
                option.innerText = `${data[i].Title} (${data[i].Count})`;
                option.setAttribute("value", data[i].Title);
                selectBox.appendChild(option);
            }
        }

        heading.appendChild(selectBox);

        this.addTourLocationEventListeners();
    }

    private createTourStageHeading() {
        const headingContainer: HTMLElement = document.querySelector("[data-main-tours-heading]") as HTMLElement;
        const heading: HTMLElement = document.createElement("h2") as HTMLElement;

        headingContainer.innerHTML = "";
        heading.innerText = this.headingTextArray[this.tourStageLoadedForType];

        if (heading.innerText.length > 0) {
            headingContainer.appendChild(heading);
        }
    }

    // UI Event Listeners and On Event Functions.

    private addTourTypeEventListeners() {
        const links: NodeListOf<HTMLElement> = document.querySelectorAll("[data-main-tours-fetch]") as NodeListOf<HTMLElement>;
        for (let i = 0; i < links.length; i++) {
            links[i].addEventListener("click", (e) => {
                if (!this.isLoading) {
                    e.preventDefault();
                    e.stopPropagation();

                    this.tourLocation = "";
                    this.assignTourType(links[i].getAttribute("data-main-tours-fetch"));

                    this.getTours(true);
                    _gaq.push(["_trackEvent", "Interaction", `Main Tours Page - View a specific type of tours (${this.headingTextArray[this.tourStageLoadedForType]})`, document.URL, 0, false]);
                }
            });
        }
    }

    private addTourLocationEventListeners() {
        const select: HTMLInputElement = document.querySelector("[data-main-tours-heading] select") as HTMLInputElement;

        select.addEventListener("change", () => {
            this.tourLocation = select.value === "All Locations" ? "" : select.value;
            this.getTours();
            _gaq.push(["_trackEvent", "Interaction", `Main Tours Page - View a specific type of tours (${this.headingTextArray[this.tourStageLoadedForType]}) in ${this.tourLocation}`, document.URL, 0, false]);
        });
    }

    private infinityScroll() {
        const infinityTrigger: HTMLElement = document.querySelector("[data-infinity-scroll-in-view]") as HTMLElement;

        if (this.isElementInViewport(infinityTrigger) && !this.isLoading && this.tourStageLoadedForType) {
            this.toggleLoading(true);
            this.getTours(false, true);
        }
    }

    // Utility.

    private assignTourType(tourType: string) {
        switch (tourType) {
            case "E":
                this.tourStageLoadedForType = TourType.ESCORTED_TOURS;
                break;
            case "C":
                this.tourStageLoadedForType = TourType.CRUISE_HOLIDAYS;
                break;
            case "D":
                this.tourStageLoadedForType = TourType.SELFDRIVE_TOURS;
                break;
            case "O":
                this.tourStageLoadedForType = TourType.PRIVATE_TOURS;
                break;
            default:
                break;
        }
    }

    private generateRequestURL(): string {
        let tourType: string = "tourtype=";
        let needChips: string = "needchips=";
        let tourLocation: string = "tourlocation=";
        let toursLoaded: string = "toursloaded=";

        tourType += this.tourTypes[this.tourStageLoadedForType];
        needChips += this.countToursLoaded() > 0 ? "N" : "Y";
        toursLoaded += this.countToursLoaded();
        tourLocation += encodeURI(this.tourLocation);

        return `/utility/tours/getTours.aspx?${tourType}&${needChips}&${tourLocation}&${toursLoaded}&increment=9`;
    }

    private toggleLoading(status: boolean) {
        const placeholder: HTMLElement = document.querySelector("[data-main-tours-loading]") as HTMLElement;
        const select: HTMLInputElement = document.querySelector("[data-main-tours-heading] select") as HTMLInputElement;

        if (status) {
            placeholder.style.display = "block";
            this.isLoading = true;
            if (select) {
                select.setAttribute("disabled", "");
            }
        } else {
            placeholder.style.display = "none";
            this.isLoading = false;
            if (select) {
                select.removeAttribute("disabled");
            }
        }
    }

    private countToursLoaded(): number {
        const tours: NodeListOf<HTMLElement> = document.querySelectorAll("[data-main-tours-tour]") as NodeListOf<HTMLElement>;

        return tours ? tours.length : 0;
    }

    private clearStage() {
        const stage: HTMLElement = document.querySelector("[data-main-tours-stage]") as HTMLElement;
        stage.innerHTML = "";
    }

    private toggleInfinityScrollTrigger(status: boolean) {
        if (status) {
            const triggerContainer: HTMLElement = document.querySelector("[data-main-tours-master]") as HTMLElement;
            const triggerEl: HTMLElement = document.createElement("div") as HTMLElement;
            triggerEl.setAttribute("data-infinity-scroll-in-view", "");

            triggerContainer.appendChild(triggerEl);
        } else {
            const trigger: HTMLElement = document.querySelector("[data-infinity-scroll-in-view]") as HTMLElement;
            if (trigger) {
                trigger.remove();
            }
        }
    }

    private isElementInViewport(el: HTMLElement): boolean {
        if (el) {
            const rect: ClientRect = el.getBoundingClientRect();

            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );
        }
        return false;
    }
}



// WEBPACK FOOTER //
// ./src/tours/ToursMainStage.ts